import {connect, WindowMessenger} from 'penpal';
import type {Connection} from 'penpal';
import {BRIDGE_INIT_MSG} from '@monei-js/shared/lib/bridge-constants';
import {EVENT, LIFECYCLE_PROPS} from './constants';
import {normalizeAliases} from './create-component';
import {toCSS} from './props';
import type {
  BridgeComponent,
  BridgeComponentOptions,
  Cancelable,
  ChildMethods,
  ComputedPropConfig,
  EventEmitterType,
  ParentMethods
} from './types';

/** Per-component iframe sandbox attributes — least-privilege per component */
const SANDBOX: Record<string, string> = {
  'monei-card-input': 'allow-scripts allow-forms allow-same-origin',
  'monei-bizum':
    'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox',
  'monei-bizum-button':
    'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox',
  'monei-paypal':
    'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox',
  'monei-payment-request': 'allow-scripts allow-forms allow-same-origin allow-popups',
  'monei-payment-modal':
    'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox',
  'monei-google-pay': 'allow-scripts allow-forms allow-same-origin allow-popups'
};

type PendingPropsUpdate = {
  dataProps: Record<string, any>;
  functionNames: string[] | undefined;
};

type RenderState = {
  dispatchMap: Map<string, Function>;
  connection: Connection<ChildMethods> | null;
  remote: {callChild: Function; updateProps: Function} | null;
  containerEl: HTMLElement | null;
  frame: HTMLIFrameElement | null;
  destroyed: boolean;
  ownsContainer: boolean;
  /** Buffered prop updates received before Penpal handshake completes */
  pendingPropsUpdate: PendingPropsUpdate | null;
  /** Keys with in-flight deferred removals (function→data transitions awaiting child delivery) */
  pendingRemovals: Set<string>;
};

/**
 * Resolve a container argument to an HTMLElement.
 */
function resolveContainer(container?: string | HTMLElement): HTMLElement {
  if (!container) {
    throw new Error('render() requires a container element or selector');
  }
  if (typeof container === 'string') {
    const el = document.querySelector<HTMLElement>(container);
    if (!el) throw new Error(`Container not found: ${container}`);
    return el;
  }
  return container;
}

/**
 * Generate a unique ID for the container element.
 */
function generateUid(tag: string): string {
  return `${tag}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Wire lifecycle props to bridge events.
 * Called before containerTemplate in the render flow.
 */
function wireLifecycleProps<P extends Record<string, any>>(
  props: P,
  event: EventEmitterType
): void {
  // Map lifecycle prop names to bridge events.
  // Late binding: reads from props at call time so updateProps() changes are reflected.
  // props is a mutable ref — Object.assign(instance.props, newProps) updates it in-place.
  const mappings: Array<[string, string]> = [
    ['onClose', EVENT.DESTROY],
    ['onDestroy', EVENT.DESTROY],
    ['onRendered', EVENT.RENDERED],
    ['onResize', EVENT.RESIZE]
  ];
  // onRender, onDisplay: zoid-internal events with no penpal equivalent — intentionally unmapped
  // onProps: handled by bridge's updateProps mechanism — not an event

  for (const [propKey, eventName] of mappings) {
    event.on(eventName, (...args: any[]) => {
      const fn = (props as Record<string, any>)[propKey];
      if (typeof fn === 'function') fn(...args);
    });
  }
}

/**
 * Execute computed prop factories and classify remaining user props into
 * dispatch map (functions) and data payload (everything else).
 * Called AFTER containerTemplate so computed factories see state mutations.
 */
function buildDataPayload<P extends Record<string, any>>(context: {
  props: P;
  options: BridgeComponentOptions<P>;
  state: Record<string, any>;
  dispatchMap: Map<string, Function>;
}): {dataProps: Record<string, any>; functionNames: string[]} {
  const {props, options, state, dispatchMap} = context;
  const dataProps: Record<string, any> = {};
  const computedPropKeys = new Set<string>(
    options.computedProps ? Object.keys(options.computedProps) : []
  );

  // Execute computed prop factories
  if (options.computedProps) {
    for (const [key, config] of Object.entries(
      options.computedProps as Record<string, ComputedPropConfig<P>>
    )) {
      const result = config.factory({props, state});
      if (config.type === 'function') {
        dispatchMap.set(key, result);
      } else {
        // type: 'value' — included in data payload sent to child
        dataProps[key] = result;
      }
    }
  }

  // Classify remaining user props
  for (const [key, value] of Object.entries(props as Record<string, any>)) {
    if (LIFECYCLE_PROPS.has(key) || computedPropKeys.has(key)) continue;
    if (typeof value === 'function') {
      dispatchMap.set(key, value);
    } else {
      dataProps[key] = value;
    }
  }

  // Collect function names from dispatch map so child can create stubs.
  // Exclude built-in methods (close, resize, show, hide, getParentDomain, focus)
  // which are always created by bridge-child — only send user/computed function names.
  const builtins = new Set(['close', 'resize', 'show', 'hide', 'getParentDomain', 'focus']);
  const functionNames = [...dispatchMap.keys()].filter((k) => !builtins.has(k));

  return {dataProps, functionNames};
}

/**
 * Implements .render(), .updateProps(), .destroy(), .close() on a bridge component instance.
 * Called once from createComponent to wire up the actual rendering logic.
 */
export function attachRenderMethods<P extends Record<string, any>>(
  instance: BridgeComponent<P>,
  options: BridgeComponentOptions<P>
): void {
  const renderState: RenderState = {
    dispatchMap: new Map(),
    connection: null,
    remote: null,
    containerEl: null,
    frame: null,
    destroyed: false,
    ownsContainer: false,
    pendingPropsUpdate: null,
    pendingRemovals: new Set()
  };

  // Add built-in dispatch methods
  let renderedTriggered = false;
  let lifecycleWired = false;
  renderState.dispatchMap.set('resize', (dimensions: {width?: number; height?: number}) => {
    const {autoResize} = options;
    const applied: {width?: number; height?: number} = {};

    // Filter dimensions based on autoResize config
    // No autoResize → allow all; autoResize set → only allow explicitly enabled dimensions
    if (dimensions.width !== undefined && (!autoResize || autoResize.width === true)) {
      applied.width = dimensions.width;
    }
    if (dimensions.height !== undefined && (!autoResize || autoResize.height === true)) {
      applied.height = dimensions.height;
    }

    // Update iframe dimensions
    if (renderState.frame) {
      if (applied.width !== undefined) {
        renderState.frame.style.width = toCSS(applied.width);
      }
      if (applied.height !== undefined) {
        renderState.frame.style.height = toCSS(applied.height);
      }
    }

    // Show iframe on first resize — child has rendered content with correct dimensions.
    // This fires as soon as Penpal connects (before updateProps), so the user sees
    // the component ~200ms earlier instead of waiting for the full handshake + prop delivery.
    if (!renderedTriggered) {
      renderedTriggered = true;
      instance.event.trigger(EVENT.RENDERED);
    }

    instance.event.trigger(EVENT.RESIZE, applied);
  });
  renderState.dispatchMap.set('close', () => instance.close());
  renderState.dispatchMap.set('show', () => {
    if (renderState.frame) renderState.frame.style.display = '';
  });
  renderState.dispatchMap.set('hide', () => {
    if (renderState.frame) renderState.frame.style.display = 'none';
  });
  renderState.dispatchMap.set('getParentDomain', () => window.location.origin);
  renderState.dispatchMap.set('focus', () => {
    if (renderState.frame) renderState.frame.focus();
  });

  /**
   * Render the component into the DOM and establish Penpal connection.
   *
   * Phase ordering:
   * 1. wireLifecycleProps — bind onClose/onRendered/onResize to bridge events
   * 2. prerenderTemplate — create loading placeholder (optional)
   * 3. containerTemplate — build wrapper DOM, may mutate `state` (e.g., state.__container for PayPal)
   * 4. buildDataPayload — run computed prop factories (read state mutations from step 3),
   *    classify user props into dispatch map (functions) and data payload (serializable values)
   * 5. appendToDOM — container or bare iframe inserted into target element
   * 6. triggerDISPLAY — notify container template that DOM is ready
   * 7. sendBootstrap — postMessage initial props to iframe before script loads
   * 8. createConnection — establish Penpal async RPC channel, deliver props via updateProps
   */
  instance.render = async (container?: string | HTMLElement): Promise<void> => {
    if (renderState.destroyed) throw new Error('Component is destroyed');
    if (renderState.frame) throw new Error('Component already rendered');

    const targetEl = resolveContainer(container);
    const uid = generateUid(options.tag);
    const doc = targetEl.ownerDocument;

    // Create iframe — append parent origin to URL hash so child can restrict allowedOrigins
    const frame = doc.createElement('iframe');
    frame.src = options.url;
    frame.setAttribute('title', options.tag.replace(/-/g, '_'));
    frame.setAttribute('allowpaymentrequest', 'true');
    frame.setAttribute('allow', 'payment');
    frame.style.border = 'none';

    // Apply per-component sandbox — fallback to restrictive default if tag unknown
    const sandbox = SANDBOX[options.tag] ?? 'allow-scripts allow-forms allow-same-origin';
    frame.setAttribute('sandbox', sandbox);

    // Phase 1: Wire lifecycle props to events (before containerTemplate)
    // Guard: only wire once — handlers use late binding (read from props at call time),
    // so the first registration works for all future prop values. Without this guard,
    // a failed render() (e.g., PayPal missing paymentId) followed by retry would
    // register duplicate handlers, causing callbacks to fire multiple times per event.
    if (!lifecycleWired) {
      wireLifecycleProps(instance.props, instance.event);
      lifecycleWired = true;
    }

    // Track event subscriptions from template phases for rollback cleanup.
    // Lifecycle handlers (Phase 1) are guarded by lifecycleWired — only template subs need tracking.
    const templateSubs: Cancelable[] = [];
    const trackedEvent: EventEmitterType = {
      on(eventName: string, handler: (...args: any[]) => void) {
        const sub = instance.event.on(eventName, handler);
        templateSubs.push(sub);
        return sub;
      },
      once(eventName: string, handler: (...args: any[]) => void) {
        const sub = instance.event.once(eventName, handler);
        templateSubs.push(sub);
        return sub;
      },
      trigger(eventName: string, ...args: any[]) {
        instance.event.trigger(eventName, ...args);
      },
      off(eventName: string, handler: (...args: any[]) => void) {
        instance.event.off(eventName, handler);
      },
      destroy() {
        instance.event.destroy();
      }
    };

    // Phases 2-4 can register event handlers (via trackedEvent) and populate dispatchMap.
    // Wrap in try-catch so failures clean up side effects — without this, a retry would
    // accumulate duplicate template handlers and stale dispatch entries.
    let placeholder: HTMLElement | undefined;
    let containerNode: HTMLElement | undefined;
    let dataProps: Record<string, any> = {};
    let functionNames: string[] = [];
    try {
      // Phase 2: Create placeholder if prerenderTemplate provided
      if (options.prerenderTemplate) {
        placeholder = options.prerenderTemplate({
          uid,
          frame,
          placeholder: undefined,
          doc,
          props: instance.props,
          event: trackedEvent,
          dimensions: options.dimensions ?? {width: '100%', height: 'auto'},
          state: instance.state,
          container: targetEl
        }) as HTMLElement | undefined;
      }

      // Phase 3: Container template — MUST run before computed prop factories
      // Container template may mutate state (e.g., state.__container for PayPal)
      if (options.containerTemplate) {
        containerNode = options.containerTemplate({
          uid,
          frame,
          placeholder,
          doc,
          props: instance.props,
          event: trackedEvent,
          dimensions: options.dimensions ?? {width: '100%', height: 'auto'},
          state: instance.state,
          container: targetEl
        }) as HTMLElement | undefined;
      }

      // Phase 4: Build dispatch map + data payload AFTER containerTemplate
      ({dataProps, functionNames} = buildDataPayload({
        props: instance.props,
        options,
        state: instance.state,
        dispatchMap: renderState.dispatchMap
      }));
    } catch (err) {
      // Clean up template event subscriptions to prevent duplicate handlers on retry
      for (const sub of templateSubs) sub.cancel();
      // Clean up user/computed dispatch entries added by buildDataPayload
      const builtins = new Set(['close', 'resize', 'show', 'hide', 'getParentDomain', 'focus']);
      for (const key of renderState.dispatchMap.keys()) {
        if (!builtins.has(key)) renderState.dispatchMap.delete(key);
      }
      throw err;
    }

    // Mark as rendered AFTER template phases that can throw (e.g., PayPal validates props
    // in containerTemplate). Setting frame earlier would block render() retries on failure.
    renderState.frame = frame;

    // Phase 7 helper: defined before try block so catch can clean up the listener.
    const targetOrigin = new URL(options.url).origin;
    const sendInitialProps = () => {
      try {
        frame.contentWindow?.postMessage(
          {type: BRIDGE_INIT_MSG, props: dataProps, functionNames},
          targetOrigin
        );
      } catch {
        // Expected for cross-origin iframes before navigation completes —
        // contentWindow origin is still the parent's origin, not the target.
        // The load event listener will retry successfully.
      }
    };

    // Wrap post-frame-set phases in try/catch so async failures (Penpal rejection,
    // updateProps error) roll back render state instead of leaving a broken instance
    // that blocks retries with "Component already rendered".
    try {
      // Phase 5: Append to DOM
      if (containerNode) {
        targetEl.appendChild(containerNode);
        renderState.containerEl = containerNode;
        renderState.ownsContainer = true;
      } else {
        // No container template — append iframe directly
        targetEl.appendChild(frame);
        renderState.containerEl = targetEl;
      }

      // Phase 6: Notify container template that DOM is ready (for backdrop transitions etc.)
      instance.event.trigger(EVENT.DISPLAY);

      // Phase 7: Send initial data + function names via sync postMessage before iframe loads script.
      // Send on each iframe navigation/load attempt — child script picks up the first one
      frame.addEventListener('load', sendInitialProps);
      // Also try sending immediately if contentWindow is available (e.g., same-origin)
      if (frame.contentWindow) {
        sendInitialProps();
      }

      // Phase 8: Establish Penpal connection
      if (!frame.contentWindow) {
        throw new Error('iframe contentWindow is not available — frame may not be in the DOM');
      }

      const messenger = new WindowMessenger({
        remoteWindow: frame.contentWindow,
        allowedOrigins: [new URL(options.url).origin]
      });

      const connection = connect<ChildMethods>({
        messenger,
        methods: {
          callParent: (name: string, args: any[]) => {
            const fn = renderState.dispatchMap.get(name);
            if (!fn) throw new Error(`Unknown parent method: ${name}`);
            return fn(...args);
          }
        } satisfies ParentMethods
      });

      renderState.connection = connection;

      // Wait for child to connect — destroy() during handshake rejects this promise
      let remote: Awaited<typeof connection.promise>;
      try {
        remote = await connection.promise;
      } catch (err) {
        if (renderState.destroyed) return;
        throw err;
      }
      if (renderState.destroyed) return;
      renderState.remote = remote;

      // Expose callChild on state so components (e.g., CardInput) can do parent→child RPC
      instance.state.__callChild = remote.callChild;

      // Send initial data + function names to child via Penpal (ensures delivery even if postMessage was missed)
      await remote.updateProps(dataProps, functionNames);
      if (renderState.destroyed) return; // destroyed while sending initial props

      // Flush any prop updates that were buffered during the Penpal handshake
      if (renderState.pendingPropsUpdate) {
        const pending = renderState.pendingPropsUpdate;
        renderState.pendingPropsUpdate = null;

        // Track in-flight removals so concurrent updateProps calls detect them (via pendingRemovals).
        // Capture original function refs so cleanup doesn't clobber concurrent updates.
        const retainedFns = new Set(pending.functionNames ?? []);
        const flushRemovals = new Map<string, Function>();
        for (const key of Object.keys(pending.dataProps)) {
          if (renderState.dispatchMap.has(key) && !retainedFns.has(key)) {
            flushRemovals.set(key, renderState.dispatchMap.get(key)!);
            renderState.pendingRemovals.add(key);
          }
        }

        await renderState.remote!.updateProps(pending.dataProps, pending.functionNames);
        if (renderState.destroyed) return;

        // Clean up deferred dispatch map removals from function→data transitions during handshake.
        // Only remove if entry hasn't been replaced by a concurrent updateProps call.
        for (const [key, originalFn] of flushRemovals) {
          renderState.pendingRemovals.delete(key);
          if (renderState.dispatchMap.get(key) === originalFn) {
            renderState.dispatchMap.delete(key);
          }
        }
      }

      // RENDERED is now triggered on first resize (earlier visibility).
      // Ensure it fires even if no resize occurs (e.g., fixed-size components).
      if (!renderedTriggered) {
        renderedTriggered = true;
        instance.event.trigger(EVENT.RENDERED);
      }

      // Clean up load listener
      frame.removeEventListener('load', sendInitialProps);
    } catch (err) {
      // Roll back render state so the instance can retry render() on recoverable failures.
      // destroy() during handshake returns early (doesn't throw), so this only catches real errors.
      frame.removeEventListener('load', sendInitialProps);
      // Cancel template event subscriptions to prevent duplicate handlers on retry.
      // Without this, each failed render adds RENDERED/RESIZE/DESTROY handlers that
      // accumulate — PayPal's DESTROY handler operates on global Buttons.instances state.
      for (const sub of templateSubs) sub.cancel();
      // Clear stale RPC handle (set after Penpal connects but before updateProps fails)
      delete instance.state.__callChild;
      if (renderState.connection) {
        renderState.connection.destroy();
        renderState.connection = null;
      }
      renderState.remote = null;
      renderState.frame = null;
      if (renderState.ownsContainer && renderState.containerEl) {
        renderState.containerEl.remove();
      } else if (frame.parentNode) {
        frame.remove();
      }
      renderState.containerEl = null;
      renderState.ownsContainer = false;
      renderState.pendingPropsUpdate = null;
      renderState.pendingRemovals.clear();
      // Clear user/computed dispatch entries so retry re-derives from current props.
      // Retain built-in methods (resize, close, show, hide, getParentDomain, focus).
      const builtins = new Set(['close', 'resize', 'show', 'hide', 'getParentDomain', 'focus']);
      for (const key of renderState.dispatchMap.keys()) {
        if (!builtins.has(key)) {
          renderState.dispatchMap.delete(key);
        }
      }
      renderedTriggered = false;
      throw err;
    }
  };

  instance.updateProps = async (newProps: Partial<P>): Promise<void> => {
    if (renderState.destroyed) throw new Error('Component is destroyed');

    // Normalize aliased prop names to canonical before processing
    const normalized = normalizeAliases(
      newProps as Record<string, any>,
      options.aliases
    ) as Partial<P>;

    // Mutate props in-place so closures see current values
    Object.assign(instance.props, normalized);

    // Before render(): props mutation is sufficient — render() will re-derive dispatch map
    // from instance.props via buildDataPayload(). Skip classification to avoid stale dispatch
    // entries (e.g., function→data transition before first render leaves orphaned dispatch keys).
    if (!renderState.frame) return;

    // Classify new props: update dispatch map for functions, collect data for child
    const newDataProps: Record<string, any> = {};
    const newFunctionNames: string[] = [];
    const computedPropKeys = new Set<string>(
      options.computedProps ? Object.keys(options.computedProps) : []
    );
    // Track function→data transitions for deferred dispatch map cleanup.
    // Captures the original function ref so concurrent updateProps calls don't clobber each other.
    const deferredRemovals = new Map<string, Function>();

    for (const [key, value] of Object.entries(normalized as Record<string, any>)) {
      if (LIFECYCLE_PROPS.has(key) || computedPropKeys.has(key)) continue;
      if (typeof value === 'function') {
        // Track newly added functions so child can create stubs.
        // Also re-track if the pending buffer has a stale data entry for this key
        // (handles function→undefined→function toggle during handshake: dispatch map
        // still has the key due to deferred deletion, but the buffer would send
        // undefined and kill the callback on flush).
        const hasStaleBufferedData =
          renderState.pendingPropsUpdate != null && key in renderState.pendingPropsUpdate.dataProps;
        // Also re-track if a concurrent updateProps is delivering a removal for this key
        // (dispatch map still has the key due to deferred deletion, but the child will
        // receive the removal first and lose its stub).
        const hasPendingRemoval = renderState.pendingRemovals.has(key);
        if (!renderState.dispatchMap.has(key) || hasStaleBufferedData || hasPendingRemoval) {
          newFunctionNames.push(key);
        }
        // Update dispatch map — no Penpal re-registration needed
        renderState.dispatchMap.set(key, value);
      } else {
        // If prop was previously a function, defer dispatch map removal.
        // During handshake: defer to keep functions available for child's pending call flush.
        // Post-connect: defer until child receives the updateProps message to avoid race
        // where child invokes the old stub before the update arrives.
        if (renderState.dispatchMap.has(key)) {
          if (renderState.remote) {
            deferredRemovals.set(key, renderState.dispatchMap.get(key)!);
            renderState.pendingRemovals.add(key);
          }
          // During handshake: kept alive, cleaned up during flush (existing behavior)
        }
        newDataProps[key] = value;
      }
    }

    const resolvedFunctionNames = newFunctionNames.length > 0 ? newFunctionNames : undefined;

    // Send data props + any new function names to child via Penpal
    if (renderState.remote) {
      await renderState.remote.updateProps(newDataProps, resolvedFunctionNames);
      // Clean up dispatch entries for function→data transitions now that child received the update.
      // Only remove if entry hasn't been replaced by a concurrent updateProps call.
      for (const [key, originalFn] of deferredRemovals) {
        renderState.pendingRemovals.delete(key);
        if (renderState.dispatchMap.get(key) === originalFn) {
          renderState.dispatchMap.delete(key);
        }
      }
    } else {
      // During handshake (frame exists, remote pending): buffer for post-handshake flush.
      // Buffer update — will be flushed after Penpal handshake completes
      if (!renderState.pendingPropsUpdate) {
        renderState.pendingPropsUpdate = {dataProps: {}, functionNames: undefined};
      }
      Object.assign(renderState.pendingPropsUpdate.dataProps, newDataProps);
      // Remove previously buffered function names that are now data props
      // (handles function → non-function transition during handshake)
      if (renderState.pendingPropsUpdate.functionNames) {
        const dataKeys = Object.keys(newDataProps);
        if (dataKeys.length > 0) {
          const dataKeySet = new Set(dataKeys);
          renderState.pendingPropsUpdate.functionNames =
            renderState.pendingPropsUpdate.functionNames.filter((n) => !dataKeySet.has(n));
          if (renderState.pendingPropsUpdate.functionNames.length === 0) {
            renderState.pendingPropsUpdate.functionNames = undefined;
          }
        }
      }
      if (resolvedFunctionNames) {
        // Clean stale data entries for keys that are now functions
        for (const name of resolvedFunctionNames) {
          delete renderState.pendingPropsUpdate.dataProps[name];
        }
        renderState.pendingPropsUpdate.functionNames = [
          ...(renderState.pendingPropsUpdate.functionNames ?? []),
          ...resolvedFunctionNames
        ];
      }
    }
  };

  instance.destroy = async (): Promise<void> => {
    if (renderState.destroyed) return;
    renderState.destroyed = true;

    instance.event.trigger(EVENT.DESTROY);

    if (renderState.connection) {
      renderState.connection.destroy();
      renderState.connection = null;
    }
    renderState.remote = null;

    // Remove DOM
    if (renderState.frame) {
      renderState.frame.remove();
      renderState.frame = null;
    }
    if (renderState.ownsContainer && renderState.containerEl) {
      renderState.containerEl.remove();
    }

    instance.event.destroy();
    renderState.dispatchMap.clear();
  };

  instance.close = async (): Promise<void> => {
    await instance.destroy();
  };

  /**
   * Expose renderState for tests
   */
  (instance as any).__renderState = renderState;
}
